import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  LinearProgress,
  Select,
  MenuItem,
  FormControl,
  useTheme
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  Timer as TimerIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon
} from "@material-ui/icons";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  card: {
    height: "100%",
    borderRadius: 12,
    boxShadow: theme.palette.type === "dark" 
      ? "0 4px 20px rgba(0,0,0,0.5)" 
      : "0 4px 20px rgba(0,0,0,0.06)",
    backgroundColor: theme.palette.background.paper,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    gap: theme.spacing(1)
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 700,
    fontSize: "1.1rem",
    color: theme.palette.text.primary,
  },
  headerIcon: {
    color: theme.palette.primary.main,
    fontSize: "1.5rem",
  },
  summaryCard: {
    padding: theme.spacing(1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.02)",
    border: `1px solid ${theme.palette.divider}`,
    textAlign: "center",
  },
  summaryValue: {
    fontSize: "1.3rem",
    fontWeight: "bold",
    marginBottom: theme.spacing(0.2),
  },
  summaryLabel: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  table: {
    minWidth: 500,
    marginTop: theme.spacing(1),
  },
  queueBadge: {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: 6,
    color: "#fff",
    fontWeight: 600,
    fontSize: "0.75rem",
  },
  progressContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  progressBar: {
    flexGrow: 1,
    height: 8,
    borderRadius: 4,
  },
  barGreen: {
    backgroundColor: "#43a047",
  },
  barOrange: {
    backgroundColor: "#fb8c00",
  },
  barRed: {
    backgroundColor: "#e53935",
  },
}));

const SlaQueueMetricsCard = () => {
  const classes = useStyles();
  const theme = useTheme();

  const [period, setPeriod] = useState("day");
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState([]);

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/time-monitoring/queue-stats", {
        params: { period }
      });
      setStats(data.stats || []);
    } catch (err) {
      console.error("Erro ao buscar métricas de SLA por fila:", err);
      setStats([]);
    } finally {
      setLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Cálculos consolidados
  const totalTickets = stats.reduce((acc, q) => acc + (q.totalTickets || 0), 0);
  const totalWithinSla = stats.reduce((acc, q) => acc + (q.withinSlaCount || 0), 0);
  const totalTimeouts = stats.reduce((acc, q) => acc + (q.timeoutCount || 0), 0);
  const overallSlaPercentage = totalTickets > 0 ? Math.round((totalWithinSla / totalTickets) * 100) : 100;
  
  const avgResponseTime = stats.length > 0 && totalTickets > 0
    ? Math.round(
        (stats.reduce((acc, q) => acc + (q.totalResponseTime || 0), 0) / totalTickets) * 10
      ) / 10
    : 0;

  return (
    <Card className={classes.card}>
      <CardContent>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.headerTitle}>
            <TimerIcon className={classes.headerIcon} />
            <div>
              <Typography variant="subtitle1" style={{ fontWeight: 700, lineHeight: 1.2 }}>
                Desempenho de SLA por Departamento
              </Typography>
              <Typography variant="caption" color="textSecondary">
                Conformidade de tempo de atendimento e limites de resposta por fila
              </Typography>
            </div>
          </div>

          <FormControl variant="outlined" size="small" style={{ minWidth: 120 }}>
            <Select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              style={{ fontSize: "0.85rem" }}
            >
              <MenuItem value="day">Hoje</MenuItem>
              <MenuItem value="week">Últimos 7 dias</MenuItem>
              <MenuItem value="month">Este Mês</MenuItem>
            </Select>
          </FormControl>
        </div>

        {/* Resumo Global */}
        <Grid container spacing={2} style={{ marginBottom: 16 }}>
          <Grid item xs={6} sm={3}>
            <div className={classes.summaryCard}>
              <div
                className={classes.summaryValue}
                style={{ color: overallSlaPercentage >= 90 ? "#43a047" : overallSlaPercentage >= 75 ? "#fb8c00" : "#e53935" }}
              >
                {overallSlaPercentage}%
              </div>
              <div className={classes.summaryLabel}>Conformidade Global</div>
            </div>
          </Grid>
          <Grid item xs={6} sm={3}>
            <div className={classes.summaryCard}>
              <div className={classes.summaryValue} style={{ color: theme.palette.primary.main }}>
                {avgResponseTime} min
              </div>
              <div className={classes.summaryLabel}>Tempo Médio Geral</div>
            </div>
          </Grid>
          <Grid item xs={6} sm={3}>
            <div className={classes.summaryCard}>
              <div className={classes.summaryValue} style={{ color: "#1976d2" }}>
                {totalTickets}
              </div>
              <div className={classes.summaryLabel}>Total Atendimentos</div>
            </div>
          </Grid>
          <Grid item xs={6} sm={3}>
            <div className={classes.summaryCard}>
              <div className={classes.summaryValue} style={{ color: totalTimeouts > 0 ? "#e53935" : "#43a047" }}>
                {totalTimeouts}
              </div>
              <div className={classes.summaryLabel}>Timeouts / Transbordos</div>
            </div>
          </Grid>
        </Grid>

        {/* Tabela de Filas */}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={160}>
            <CircularProgress size={32} />
          </Box>
        ) : stats.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" height={100}>
            <Typography variant="body2" color="textSecondary">
              Nenhuma fila encontrada ou sem dados de atendimento para o período selecionado.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table size="small" className={classes.table}>
              <TableHead>
                <TableRow>
                  <TableCell style={{ fontWeight: 700 }}>Departamento / Fila</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>SLA Limite</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>Atendimentos</TableCell>
                  <TableCell style={{ fontWeight: 700, minWidth: 150 }}>% no Prazo</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>Tempo Médio</TableCell>
                  <TableCell align="center" style={{ fontWeight: 700 }}>Estouros (Timeout)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.map((row) => {
                  const pct = row.slaPercentage;
                  const barColor = pct >= 90 ? "#43a047" : pct >= 75 ? "#fb8c00" : "#e53935";

                  return (
                    <TableRow key={row.queueId} hover>
                      <TableCell>
                        <span
                          className={classes.queueBadge}
                          style={{ backgroundColor: row.queueColor || theme.palette.primary.main }}
                        >
                          {row.queueName}
                        </span>
                      </TableCell>
                      <TableCell align="center">
                        {row.slaMinutes ? (
                          <Chip
                            size="small"
                            label={`${row.slaMinutes} min`}
                            variant="outlined"
                            style={{ fontWeight: 600, fontSize: "0.75rem" }}
                          />
                        ) : (
                          <Typography variant="caption" color="textSecondary">
                            Padrão Empresa
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center" style={{ fontWeight: 600 }}>
                        {row.totalTickets}
                      </TableCell>
                      <TableCell>
                        <div className={classes.progressContainer}>
                          <LinearProgress
                            variant="determinate"
                            value={pct}
                            className={classes.progressBar}
                            classes={{
                              barColorPrimary: pct >= 90 ? classes.barGreen : pct >= 75 ? classes.barOrange : classes.barRed
                            }}
                            style={{
                              backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.1)" : "#e0e0e0"
                            }}
                          />
                          <Typography variant="caption" style={{ fontWeight: 700, minWidth: 32, color: barColor }}>
                            {pct}%
                          </Typography>
                        </div>
                      </TableCell>
                      <TableCell align="center" style={{ fontWeight: 600 }}>
                        {row.averageResponseTimeMinutes} min
                      </TableCell>
                      <TableCell align="center">
                        {row.timeoutCount > 0 ? (
                          <Chip
                            size="small"
                            label={row.timeoutCount}
                            style={{ backgroundColor: "#e53935", color: "#fff", fontWeight: 700, height: 20 }}
                          />
                        ) : (
                          <Typography variant="body2" color="textSecondary">
                            0
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default SlaQueueMetricsCard;
